import type { CSSProperties } from "react";
import type {
  CommercialDocumentOwnerView,
  CommercialEconomicOwnerView,
  Schema,
} from "../types/generated";
import { formatMoney } from "../utils";

const stack: CSSProperties = { display: "grid", gap: "0.75rem" };
const fact: CSSProperties = { margin: 0 };
const muted: CSSProperties = {
  ...fact,
  color: "var(--ink-2)",
  fontSize: "0.875rem",
};

export const moduleOfferGateLabels: Record<string, string> = {
  prices_currencies_units: "Prices, currencies and billable units",
  allowances_limits: "Allowances and limits",
  annual_discount: "Annual discount approval",
  trial_grace_refund: "Trial, grace and refund policies",
  baseline_boundaries: "Baseline capability boundaries",
  legal_entity_jurisdiction: "Legal entity and jurisdiction",
  youth_policy: "Youth policy",
  retention: "Retention policy",
  delegated_finance: "Delegated finance authority",
  provider_routing: "Payment routing",
  payment_recovery: "Payment recovery",
  obligation_handoffs: "Existing obligation handoffs",
};

/** Display-unit conversion only, never a price/discount/fee calculation. */
export type ModuleOfferEconomicsProps =
  | { termsFormat: "legacy_fixed_v1"; terms: CommercialEconomicOwnerView }
  | { termsFormat: "software_terms_v2"; terms: Schema["CommercialSoftwareEconomicOwnerViewV2"] };

export function ModuleOfferEconomics(props: ModuleOfferEconomicsProps) {
  const terms = props.terms;
  const money = props.termsFormat === "legacy_fixed_v1"
    ? props.terms.software_price : props.terms.billing_period_total;
  return (
    <section style={stack} aria-label="Stored module economics">
      <p style={fact}>
        {formatMoney(
          money.amount_minor / 100,
          money.currency,
        )}{" "}
        — {terms.cadence}
      </p>
      {props.termsFormat === "software_terms_v2" ? <p style={fact}>
        {props.terms.renewal_mode === "auto_renew" ? "Auto-renew" : "Fixed term"}.
        Approved billing-period total; quantity does not multiply this amount.
      </p> : <p style={fact}>Historical fixed-term edition; no new purchase price is inferred.</p>}
      <p style={fact}>
        Billable unit: {terms.billable_unit}. Quantity: {terms.unit_quantity}.
        Contract periods: {terms.contract_periods ?? "No fixed cap (explicit auto-renew)"}.
      </p>
      {terms.allowances.map((limit, index) => (
        <div key={index} style={{ ...stack, gap: "0.25rem" }}>
          <p style={{ ...fact, fontWeight: 600 }}>{limit.unit_key}</p>
          <p style={fact}>
            Included: {limit.included_quantity ?? "Unresolved"}; maximum:{" "}
            {limit.maximum_quantity ?? "Unresolved"}.
          </p>
          <p style={muted}>
            Period: {limit.period.replaceAll("_", " ")}. Over-limit behavior:{" "}
            {limit.over_limit_behavior.replaceAll("_", " ")}.
          </p>
        </div>
      ))}
      {!terms.allowances.length ? (
        <p style={fact}>
          No allowance entries are listed; this does not imply unlimited use.
        </p>
      ) : null}
      <p style={fact}>
        Trial: {terms.trial_days} days. Grace: {terms.grace_days} days.
      </p>
      <p style={fact}>
        Renewal policy reference: {terms.renewal_policy_reference}
      </p>
      <p style={fact}>
        Refund policy reference: {terms.refund_policy_reference}
      </p>
      <p style={fact}>
        Termination policy reference: {terms.termination_policy_reference}
      </p>
      <p style={fact}>
        Retention policy reference: {terms.retention_policy_reference}
      </p>
    </section>
  );
}

/** React text nodes only: exact stored HTML is readable source, not executed. */
export function ModuleOfferExactDocument({
  document,
}: {
  document: CommercialDocumentOwnerView;
}) {
  return (
    <section style={stack} aria-label="Exact immutable document">
      <h2 style={{ ...fact, fontSize: "1.125rem", fontWeight: 600 }}>
        Exact agreement content
      </h2>
      <p style={muted}>
        {document.media_type} · {document.language}
      </p>
      <pre
        style={{
          margin: 0,
          whiteSpace: "pre-wrap",
          overflowWrap: "anywhere",
          fontFamily: "inherit",
        }}
      >
        {document.content}
      </pre>
      <p style={muted}>Document SHA-256</p>
      <p
        style={{
          ...fact,
          overflowWrap: "anywhere",
          fontFamily: "var(--mono, monospace)",
        }}
      >
        {document.content_sha256}
      </p>
    </section>
  );
}
